/**
 * <s-description-variant-links> — Lets photos (or any links) in the product description that
 * point at one of this product's variants (?variant=<id>) select that variant on the page
 * instead of reloading it. The variant picker then refreshes the form, title and gallery,
 * and the gallery leads with the chosen variant's photo.
 *
 * Element dataset:
 *   data-variant-ids — Comma-separated variant IDs that belong to this product.
 *   data-gallery-id  — id of the product's <s-product-gallery>, scrolled into view on phones
 *                      and tablets where the gallery sits above the description.
 *
 * External coupling:
 *   s-variant-picker.selectVariant(id) — public method on the product's variant picker.
 *   Falls back to normal link navigation when the picker is missing, so the ?variant= page
 *   load still shows the right photo.
 */
class DescriptionVariantLinks extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('click', (event) => this.#onClick(event), { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #onClick(event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link || !this.contains(link)) return;

    let url;
    try {
      url = new URL(link.href, location.href);
    } catch {
      return;
    }

    const variantId = url.searchParams.get('variant');
    const ownIds = (this.dataset.variantIds || '').split(',');
    if (!variantId || !ownIds.includes(variantId)) return;

    const picker = document.querySelector('[data-product-form] s-variant-picker');
    if (!picker || typeof picker.selectVariant !== 'function') return;

    event.preventDefault();
    picker.selectVariant(variantId);
    this.#revealGallery();
  }

  #revealGallery() {
    const gallery = this.dataset.galleryId ? document.getElementById(this.dataset.galleryId) : null;
    if (!gallery) return;

    const rect = gallery.getBoundingClientRect();
    const fullyVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
    if (fullyVisible) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    gallery.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }
}
customElements.define('s-description-variant-links', DescriptionVariantLinks);
